import { NextPage } from 'next'
import { Container } from '@ucheba/ui/components/Container/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import React, { memo, useMemo } from 'react'
import { useMediaBreakpoints } from '@ucheba/utils/hooks/useMediaBreakpoints'
import { useOnlyClientRendering } from '@ucheba/utils/hooks/useOnlyClientRendering'
import { EProgramTypes } from '@ucheba/utils/types/program'
import { useRouter } from 'next/router'
import { analyticEvent } from '@ucheba/utils/helpers/analytics/events'
import {
  EAnalyticEvents,
  EAnalyticEventTargets,
  EAnalyticSections,
} from '@ucheba/utils/helpers/analytics/events/types'
import Link from 'next/link'
import { Button } from '@ucheba/ui/components/Button/desktop'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import { specialitiesSearchSelectors } from '@ucheba/store/specialities/search'
import { useSelector } from 'react-redux'
import { Banner } from '../../../../../components/Banner/desktop'
import { ISpecialitiesPageProps } from '../types'
import SpecialitiesPageHeader from './SpecialitiesPageHeader'
import SpecialitiesPageRubrics from './SpecialitiesPageRubrics'
import SpecialitiesPageEgeVariants from './SpecialitiesPageEgeVariants'
import { ConsultationPromo } from '../../../../../components/ConsultationPromo/desktop'
import {
  EApplicationFormProductTypes,
  EApplicationFunnels,
} from '../../../../../components/AppliactionForm/types'
import { ETypeRequest } from '../../../ege/index/types'
import LidMagnetForm from '../../../../../components/LidMagnetForm/desktop'
import { useSectionSlug } from '../bll'
import { SpecialitiesList } from '../../../../../components/SpecialitiesList/desktop'
import { specialitiesSearchPath } from '../constants'

/** Страница каталога специальностей */
const SpecialitiesPage: NextPage<ISpecialitiesPageProps> = () => {
  const searchResults = useSelector(specialitiesSearchSelectors.selectAll)
  const isLgAndUp = useMediaBreakpoints('lgAndUp')
  const isClient = useOnlyClientRendering()
  const { query, pathname } = useRouter()
  const param = query['topProgramTypes[]']
  const { isInstitutionsSection } = useSectionSlug()

  const params = useMemo(() => {
    if (param) {
      return {
        ...query,
        programTypes: param,
      }
    }

    return {
      ...query,
      'programTypes[]': [EProgramTypes.higher],
    }
  }, [query, param])

  const institutionItem = useMemo(() => {
    if (pathname.includes('/vocational')) {
      return ['Специальности в колледжах', `${specialitiesSearchPath}/vocational`]
    }
    return ['Специальности в вузах', `${specialitiesSearchPath}/higher`]
  }, [pathname])

  return (
    <Container>
      <SpecialitiesPageHeader title={institutionItem[0]} />

      <Spacing spacing='large' />

      <Grid as='main'>
        <GridCol lg={18} uxs={24}>
          <SpecialitiesPageRubrics />

          <Spacing spacing='xlarge' />

          {/* eslint-disable-next-line sonarjs/no-all-duplicated-branches */}
          {!isInstitutionsSection ? (
            <LidMagnetForm
              title='Хочешь сдать ОГЭ на&nbsp;5+ и&nbsp;поступить в&nbsp;колледж на&nbsp;бюджет?'
              text='Поможем подготовиться к&nbsp;ОГЭ на&nbsp;высокие баллы с&nbsp;гарантией поступления в&nbsp;онлайн-школе Учеба.ру'
              buttonText='Подготовиться к ОГЭ'
              size='small'
              funnel={EApplicationFunnels.speciality_rubrics}
              productType={EApplicationFormProductTypes.oge}
              requestTarget={ETypeRequest.oge_lead_request}
              onSubmitForm={() => {
                analyticEvent({
                  targets: [EAnalyticEventTargets.ym, EAnalyticEventTargets.ur],
                  eventName: EAnalyticEvents.leadFormRequestSpeciality,
                  section: EAnalyticSections.leadMagnetForm,
                })
              }}
              list={[
                'Расскажем о системе эффективной подготовки',
                'Познакомим с онлайн-школой и платформой',
                'Ответим на вопросы и запишем на занятия',
              ]}
            />
          ) : (
            <LidMagnetForm
              title='Хочешь сдать ЕГЭ на&nbsp;80+ и&nbsp;поступить в&nbsp;вуз на&nbsp;бюджет?'
              text='Поможем подготовиться к&nbsp;ЕГЭ на&nbsp;высокие баллы с&nbsp;гарантией поступления в&nbsp;онлайн-школе Учеба.ру'
              buttonText='Подготовиться к ЕГЭ'
              size='small'
              funnel={EApplicationFunnels.speciality_rubrics}
              productType={EApplicationFormProductTypes.ege}
              requestTarget={ETypeRequest.ege_lead_request}
              onSubmitForm={() => {
                analyticEvent({
                  targets: [EAnalyticEventTargets.ym, EAnalyticEventTargets.ur],
                  eventName: EAnalyticEvents.leadFormRequestSpeciality,
                  section: EAnalyticSections.leadMagnetForm,
                })
              }}
              list={[
                'Расскажем о системе эффективной подготовки',
                'Познакомим с онлайн-школой и платформой',
                'Ответим на вопросы и запишем на занятия',
              ]}
            />
          )}

          {isClient && !isLgAndUp && (
            <>
              <Spacing spacing='xlarge' />

              <ConsultationPromo orientation='horizontal' />
            </>
          )}

          {isInstitutionsSection && <SpecialitiesPageEgeVariants />}

          <Spacing spacing='uxlarge' />

          {searchResults.length && (
            <>
              <Heading h2='Популярные специальности' spacing='large' />

              <SpecialitiesList
                items={searchResults}
                addSearchQueryToInstitutionHref
                params={params}
              />

              <Spacing spacing='xlarge' />
              <Link href={institutionItem[1]}>
                <Button block view='outline' color='blue'>
                  Показать все популярные специальности
                </Button>
              </Link>
            </>
          )}
        </GridCol>

        {isClient && isLgAndUp && (
          <GridCol lg={6} uxs={24} as='aside'>
            <div style={{ position: 'sticky', top: '0' }}>
              <Banner id={3} extraParams={params} />
            </div>
          </GridCol>
        )}
      </Grid>

      <Spacing spacing='uxlarge' />

      <Banner id={6} extraParams={params} />

      <Banner id={14} extraParams={params} />
    </Container>
  )
}

export default memo(SpecialitiesPage)
